/**
 * PoolBoardRoute — `/oppgavebunken`, Oppgaver.
 *
 * #154 (redesign steg 4b, design/screens/Stell_Oppgaver.dc.html):
 *   1. datopanelet: «Oppgaver», uke og streak, Foreslå-knappen og avataren
 *      (til Meg), og ukestripen med streak-prikker (useStreak);
 *   2. utvalget: Mine / Ledige / Alle som chips med tellere, og personfilteret
 *      (BoardFilters);
 *   3. seksjonene Mine nå → Ledige → Lekser (boardFilter.sectionBoard), med
 *      kortene som TaskRow-rader;
 *   4. nederst: Ferdig (historikken, CompletedList) og lenken til /lekser.
 * Skallet legger ikke sin egen topp over Oppgaver (`drawsOwnHeader`).
 *
 * One feed, several lenses. The route reads `GET /api/chores/board` — every
 * live instance (available + claimed + awaiting_approval) across chores AND
 * homework — and derives everything else in memory.
 *
 * «Ta flytter aldri kortet»: et kort du tar, festes der det står (`pinned`)
 * til du går fra tavla eller endrer utvalget. Regelen står i boardFilter.ts.
 * Knappen viser «Tar …» til serveren har svart og tavla er hentet på nytt, og
 * så «Fullfør» på samme plass. Ingen optimistisk UI (CLAUDE.md, INV-CLAIM-2).
 *
 * Behaviour notes:
 *   - useChoresWS() invalidates the ['chores'] family, which covers
 *     ['chores','board'] by prefix — the board stays live without polling.
 *   - "Fullfør" opens <ChoreCompleteSheet> rather than calling complete()
 *     directly, so proof_required templates get their photo-upload step.
 *   - /lekser stays mounted as its own route; homework here is an additional
 *     surface, not a replacement, so existing links and bookmarks survive.
 *
 * Viewer identity: read from /api/auth/me on mount. Until the round-trip lands
 * the route renders with an anonymous viewer, so the kitchen tablet's authless
 * dashboard tap-through still works (DASH-06).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { MeAvatarLink } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { Button, DatePanel, EmptyState, Icon, WeekStrip } from '../../components/primitives';
import { formatWeekNumber } from '../../i18n/formatters';
import { useToday } from '../dashboard/tiles/DateTile';
import { streakDayCheck } from '../streaks/streakDays';
import { useStreak } from '../streaks/useStreakQueries';
import { ChoreCard } from './ChoreCard';
import type { Viewer } from './ChoreCard';
import { HomeworkCard } from './HomeworkCard';
import { useChoreActions } from './useChoreActions';
import { useChoresWS } from './useChoresWS';
import { useBoard, useCompletedChores, type BoardChoreInstance } from './useChoresQuery';
import { useFamilyMembers } from './useFamilyMembers';
import { BoardFilters } from './BoardFilters';
import { filterBoard, groupByCategory, sectionBoard, type BoardScope } from './boardFilter';
import { CompletedList } from './CompletedList';
import { MyChoresStrip } from './MyChoresStrip';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { ChoreCompleteSheet } from './ChoreCompleteSheet';
import { ProposeChoreSheet } from './ProposeChoreSheet';

/** Days of history the Ferdig view shows. Matches the server default. */
const HISTORY_DAYS = 30;

const NO_PINS: ReadonlySet<string> = new Set();

function without(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(id);
  return next;
}

/** Radene i seksjonene: én kolonne på telefon, to fra nettbrett. */
const ROWS = 'grid grid-cols-1 gap-2.5 md:grid-cols-2';

type SessionMember = {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  birth_date?: string;
};

function ageYearsAt(birthIso: string | undefined, atIso: string): number {
  if (!birthIso) return Number.POSITIVE_INFINITY;
  const birth = new Date(birthIso);
  const at = new Date(atIso);
  let years = at.getUTCFullYear() - birth.getUTCFullYear();
  const m = at.getUTCMonth() - birth.getUTCMonth();
  if (m < 0 || (m === 0 && at.getUTCDate() < birth.getUTCDate())) years -= 1;
  return years;
}

function useViewer(): Viewer | null {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', {
          credentials: 'same-origin',
        });
        if (!r.ok) return;
        const body = (await r.json()) as { member?: SessionMember };
        if (cancelled || !body.member) return;
        setViewer({
          id: body.member.id,
          shortName: body.member.short_name,
          // Server-side queryBoard already computes age_gated; ageYears here
          // only pre-fills the Viewer prop for ChoreCard's variant resolution,
          // which reads the age_gated flag rather than recomputing.
          ageYears: ageYearsAt(body.member.birth_date, new Date().toISOString()),
        });
      } catch {
        // Network failure — leave viewer null; the board renders anonymously.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return viewer;
}

const ANON_VIEWER: Viewer = { id: 'anonymous', ageYears: 0 };

/** Datopanelet: uke, streak og ukestripen for den som ser på. */
function BoardPanel({
  viewerId,
  onPropose,
}: {
  viewerId: string;
  onPropose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const today = useToday();
  const streak = useStreak(viewerId);
  const days = streak.data?.current_streak ?? 0;
  const week = t('chores.panel.week', { week: formatWeekNumber(today) });
  const isDone = useMemo(() => streakDayCheck(streak.data), [streak.data]);

  return (
    <DatePanel
      title={t('nav.chores')}
      label={t('chores.panel.label')}
      subtitle={days > 0 ? `${week} · ${t('chores.panel.streak', { count: days })}` : week}
      className="md:mx-8 md:mt-4 md:rounded-2xl"
      action={
        <div className="flex items-center gap-2">
          {/* Panelet har alltid mørk tekst, så knappen er fylt on-panel i
              begge temaer — ikke Button sin solid, som snur i mørkt tema. */}
          <button
            type="button"
            onClick={onPropose}
            data-testid="propose-chore-open"
            aria-label={t('chores.propose.cta')}
            className="flex h-11 items-center gap-1.5 rounded-pill bg-on-panel px-3.5 text-sm font-extrabold text-on-panel-solid"
          >
            <Icon.Plus size={18} />
            {t('chores.propose.button')}
          </button>
          {/* #187: tilbakemelding fra skjermen, på panelets flate. */}
          <FeedbackWidget variant="icon" tone="panel" />
          <MeAvatarLink />
        </div>
      }
    >
      <WeekStrip weekOf={today} today={today} isDone={isDone} />
    </DatePanel>
  );
}

export function PoolBoardRoute(): ReactNode {
  const { t } = useTranslation();
  useChoresWS();
  const viewer = useViewer();
  const board = useBoard();
  const members = useFamilyMembers();
  const actions = useChoreActions();

  const [proposeOpen, setProposeOpen] = useState(false);
  const [tab, setTab] = useState<'open' | 'done'>('open');
  const [scope, setScope] = useState<BoardScope>('all');
  const [whoId, setWhoId] = useState<string | null>(null);
  // Kort seeren har trykket «Ta» på, festet der de står (boardFilter.ts).
  // Lokal tilstand: en ny montering (navigasjon) løsner dem.
  const [pinned, setPinned] = useState<ReadonlySet<string>>(NO_PINS);
  // Claim som venter på serveren, per kort. Mutasjonens `variables` følger bare
  // siste kall, så to «Ta» rett etter hverandre ville ellers sluppet det første
  // kortet løs mens det fortsatt venter.
  const [claiming, setClaiming] = useState<ReadonlySet<string>>(NO_PINS);
  // The instance whose "Fullfør" was tapped. Completing goes through
  // <ChoreCompleteSheet> rather than calling actions.complete directly, because
  // a template with proof_required 'optional'/'required' needs the photo-upload
  // step before it can be sent for approval.
  const [completeFor, setCompleteFor] = useState<BoardChoreInstance | null>(null);

  // The Ferdig view is history — a deliberate visit, so it stays unfetched until
  // opened. The board is the kitchen tablet's hot path.
  const completed = useCompletedChores(HISTORY_DAYS, tab === 'done');
  const completedRows = completed.data ?? [];

  const allRows = useMemo(() => board.data ?? [], [board.data]);
  const viewerId = viewer?.id ?? '';

  const visible = useMemo(
    () => filterBoard(allRows, { scope, viewerId, memberId: whoId, pinned }),
    [allRows, scope, viewerId, whoId, pinned],
  );
  const sections = useMemo(
    () => sectionBoard(visible, { viewerId, pinned }),
    [visible, viewerId, pinned],
  );
  const choreGroups = useMemo(() => groupByCategory(sections.open), [sections.open]);
  // Category has been in the schema and the create/update bodies all along but
  // was never settable in the admin form, so today every template lands in the
  // single null group. Headings would be noise until that changes.
  const showGroupHeadings = choreGroups.length > 1;

  // Tellerne på chipene gjelder hele tavla, ikke personfilteret.
  const counts = useMemo(
    () => ({
      mine: filterBoard(allRows, { scope: 'mine', viewerId }).length,
      open: allRows.filter((r) => r.status === 'available').length,
    }),
    [allRows, viewerId],
  );

  // Et nytt utvalg er en ny visning, så festede kort legger seg på plass.
  function changeScope(next: BoardScope): void {
    setScope(next);
    setPinned(NO_PINS);
  }
  function changeMember(next: string | null): void {
    setWhoId(next);
    setPinned(NO_PINS);
  }

  function claim(id: string): void {
    if (!viewer || claiming.has(id)) return;
    // Festes før serveren svarer, så kortet aldri rekker å flytte seg når den
    // nye tavla kommer. Selve kortet endres ikke før serveren har svart.
    setPinned((prev) => new Set(prev).add(id));
    setClaiming((prev) => new Set(prev).add(id));
    // mutateAsync gir ett løfte per kall, også når claim overlapper. Feilen er
    // allerede vist som toast i useChoreActions; her løsnes bare kortet.
    actions.claim
      .mutateAsync(id)
      .catch(() => setPinned((prev) => without(prev, id)))
      .finally(() => setClaiming((prev) => without(prev, id)));
  }

  function renderCard(row: BoardChoreInstance): ReactNode {
    const shared = {
      viewer: viewer ?? ANON_VIEWER,
      spinner: claiming.has(row.id),
      onClaim: () => claim(row.id),
      onComplete: () => {
        if (!viewer) return;
        setCompleteFor(row);
      },
    };
    if (row.kind === 'homework') {
      return (
        <HomeworkCard
          key={row.id}
          // The board feed names the claimant after its DB column; the
          // homework feed uses claimant_short_name. Bridge it here rather than
          // shipping both keys from the server.
          instance={{ ...row, claimant_short_name: row.claimed_by_short_name ?? null }}
          showSubject
          {...shared}
        />
      );
    }
    return <ChoreCard key={row.id} instance={row} {...shared} />;
  }

  /** Scope-aware empty copy — "nothing available" and "you hold nothing" are
   *  different situations and deserve different next steps. */
  function emptyCopy(): { heading: string; body: string } {
    if (whoId) {
      return {
        heading: t('chores.scopeEmpty.member.heading'),
        body: t('chores.scopeEmpty.member.body'),
      };
    }
    if (scope === 'mine') {
      return {
        heading: t('chores.scopeEmpty.mine.heading'),
        body: t('chores.scopeEmpty.mine.body'),
      };
    }
    if (scope === 'open') {
      return {
        heading: t('chores.scopeEmpty.open.heading'),
        body: t('chores.scopeEmpty.open.body'),
      };
    }
    return { heading: t('chores.empty.heading'), body: t('chores.empty.body') };
  }

  return (
    <main data-testid="pool-board" className="min-h-dvh pb-6">
      <BoardPanel viewerId={viewerId} onPropose={() => setProposeOpen(true)} />

      <div className="px-5 md:px-8">
        {tab === 'done' ? (
          <section data-testid="pool-done" aria-labelledby="board-section-done" className="pt-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <SectionLabel id="board-section-done">{t('chores.tabs.done')}</SectionLabel>
              <Button
                variant="secondary"
                size={44}
                data-testid="pool-tab-open"
                onClick={() => setTab('open')}
              >
                <Icon.ChevL size={18} />
                {t('chores.done.back')}
              </Button>
            </div>
            <p className="mb-3 text-sm font-semibold text-ink">
              {t('chores.completed.count', {
                count: completedRows.length,
                days: HISTORY_DAYS,
              })}
            </p>
            <CompletedList rows={completedRows} />
          </section>
        ) : (
          <>
            <BoardFilters
              scope={scope}
              onScopeChange={changeScope}
              counts={counts}
              memberId={whoId}
              onMemberChange={changeMember}
              members={members.data ?? []}
            />

            {visible.length === 0 ? (
              <section data-testid="pool-board-empty" className="pt-3">
                <EmptyState variant="page" {...emptyCopy()} />
              </section>
            ) : (
              <div className="flex flex-col gap-5 pt-3">
                <MyChoresStrip rows={sections.mine} renderCard={renderCard} />

                {sections.open.length > 0 ? (
                  <section data-testid="board-open-section" aria-labelledby="board-section-open">
                    <SectionLabel id="board-section-open">{t('chores.sections.open')}</SectionLabel>
                    <div className="flex flex-col gap-4">
                      {/* Chores, grouped by category. One group → no heading. */}
                      {choreGroups.map((group) => (
                        <div
                          key={group.key ?? '__uncategorised__'}
                          data-testid={
                            showGroupHeadings
                              ? `board-group-${group.key ?? 'other'}`
                              : 'pool-board-grid'
                          }
                        >
                          {showGroupHeadings ? (
                            <SectionLabel as="h3">{group.key ?? t('chores.group.other')}</SectionLabel>
                          ) : null}
                          <div className={ROWS}>{group.rows.map(renderCard)}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}

                {/* Lekser — own section because homework carries a subject and a
                    real deadline. */}
                {sections.homework.length > 0 ? (
                  <section data-testid="board-homework-section" aria-labelledby="board-section-homework">
                    <SectionLabel id="board-section-homework">{t('chores.homeworkSection')}</SectionLabel>
                    <div className={ROWS}>{sections.homework.map(renderCard)}</div>
                  </section>
                ) : null}
              </div>
            )}

            {/* Ferdig og Lekser ligger nederst: historikken er et bevisst
                besøk, og /lekser viser også leverte lekser. */}
            <nav aria-label={t('chores.more.label')} className="mt-6 flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size={44}
                data-testid="pool-tab-done"
                onClick={() => setTab('done')}
              >
                {t('chores.tabs.done')}
                <Icon.ChevR size={18} />
              </Button>
              <Link
                to="/lekser"
                data-testid="pool-to-homework"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-pill border border-secondary-line bg-secondary px-4.5 text-sm font-extrabold text-ink hover:bg-inset"
              >
                {t('chores.lekserLink')}
                <Icon.ChevR size={18} />
              </Link>
            </nav>
          </>
        )}
      </div>

      {/* Completion sheet — photo proof + "Send til godkjenning". Mounted only
          when a card is pending completion so the sheet's own open/close effect
          resets its upload state between instances. */}
      {completeFor && viewer ? (
        <ChoreCompleteSheet instance={completeFor} open onClose={() => setCompleteFor(null)} />
      ) : null}

      <ProposeChoreSheet open={proposeOpen} onClose={() => setProposeOpen(false)} />
    </main>
  );
}
